import React, { useState } from 'react';
import { apiService } from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';
import '../shared.css';
import '../KG/Modal.css';

interface CreatePipelineRunModalProps {
  kgId: string;
  versionId: string;
  orgId: string;
  onClose: () => void;
}

type Step = 'confirm' | 'running' | 'success' | 'error';

const CreatePipelineRunModal: React.FC<CreatePipelineRunModalProps> = ({
  kgId,
  versionId,
  orgId,
  onClose,
}) => {
  const batchName = `${kgId}_v${versionId}_elastic_etl`;
  const now = new Date();
  const dateStr = `${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}-${now.getFullYear()} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const runName = `version ${versionId} - ${dateStr}`;

  const [step, setStep] = useState<Step>('confirm');
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState('');
  const [runId, setRunId] = useState('');

  const handleStart = async () => {
    const pipelineId = apiService.getElasticEtlPipelineId();
    const apiKey = apiService.getApiKey();

    if (!pipelineId) {
      setError('No pipeline ID configured for the current environment.');
      setStep('error');
      return;
    }
    if (!apiKey) {
      setError('API key not available.');
      setStep('error');
      return;
    }

    setStep('running');
    setError('');

    try {
      // Search for existing batch
      setStatusMessage('Searching for existing batch...');
      const searchResult: any = await apiService.searchBatches(pipelineId, orgId, batchName);
      const batches: any[] = searchResult?.data ?? [];
      const existingBatch = batches.find(
        (b: any) => b.attributes?.name === batchName,
      );

      let batchId: string;
      if (existingBatch) {
        batchId = existingBatch.id;
        setStatusMessage(`Using existing batch: ${batchName}`);
      } else {
        setStatusMessage('Creating batch...');
        const createResult: any = await apiService.createBatch(pipelineId, orgId, batchName);
        batchId = createResult?.data?.id;
        if (!batchId) throw new Error('Batch creation returned no ID.');
        setStatusMessage(`Batch created: ${batchName}`);
      }

      // Create run
      setStatusMessage('Creating pipeline run...');
      const runResult: any = await apiService.createPipelineRun(batchId, runName, kgId, versionId, apiKey);
      const createdRun = runResult?.data?.[0];
      const createdRunId = createdRun?.id ?? '';
      setRunId(createdRunId);
      setStep('success');
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to create pipeline run'));
      setStep('error');
    }
  };

  return (
    <div className="modal-overlay" onClick={step === 'running' ? undefined : onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create Pipeline Run</h2>
          {step !== 'running' && (
            <button className="modal-close" onClick={onClose}>
              &times;
            </button>
          )}
        </div>

        <div className="modal-body">
          {(step === 'confirm' || step === 'running') && (
            <>
              <div className="form-group">
                <label>Batch Name</label>
                <div className="pipeline-run-value">{batchName}</div>
                <div className="pipeline-run-hint">
                  An existing batch with this name will be reused; otherwise a new batch will be created.
                </div>
              </div>
              <div className="form-group">
                <label>Run Name</label>
                <div className="pipeline-run-value">{runName}</div>
              </div>
              {step === 'running' && (
                <div className="pipeline-run-status">
                  <span className="pipeline-run-spinner" />
                  {statusMessage}
                </div>
              )}
            </>
          )}

          {step === 'success' && (
            <div className="pipeline-run-success">
              <div className="pipeline-run-success-icon">✓</div>
              <p>Pipeline run created successfully.</p>
              {runId && <p className="pipeline-run-id">Run ID: {runId}</p>}
            </div>
          )}

          {step === 'error' && (
            <>
              <div className="error-message">{error}</div>
            </>
          )}
        </div>

        <div className="modal-footer">
          {step === 'confirm' && (
            <>
              <button className="tab-btn tab-btn--ghost" onClick={onClose}>
                Cancel
              </button>
              <button className="tab-btn tab-btn--primary" onClick={handleStart}>
                Start Run
              </button>
            </>
          )}
          {step === 'running' && (
            <button className="tab-btn tab-btn--ghost" disabled>
              Running...
            </button>
          )}
          {(step === 'success' || step === 'error') && (
            <button className="tab-btn tab-btn--primary" onClick={onClose}>
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreatePipelineRunModal;
